import { MatSnackBar } from '@angular/material/snack-bar';
import { Injectable } from '@angular/core';
import { Store, Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';

import { Observable, of, combineLatest, pipe } from 'rxjs';
import { warnOnErrorWithRetryOption$ } from '@core/services/project/project-detail.effects';
import { selectLoadedProjectId } from '@core/state/ui/project/project-ui.selectors';
import { getSendableStudyTableParamsDto } from '../../state/ui/study/study-ui.selectors';
import { studySchema } from '@core/state/entities/study/study.entity';
import { map, switchMap, tap, filter } from 'rxjs';
import { take } from 'rxjs';
import { selectCurrentProject } from '@app/core/state/entities/project/project.selectors';
import { StudiesTableHttpService } from '@core/services/study-table/study-table.service';
import { StudyHttpService } from '@core/services/api-client.generated';
import { IncomingTableParamsDtoOfStudyFilters } from '../api-client.generated';
import { studiesActions } from '@core/actions/studies.actions';

export const projectStudyIncomingDtoSchema = {
  data: [studySchema],
};

export const combineWithNext = <TOriginal, TIncoming>(
  combineWithObservable$: Observable<TIncoming>
) =>
  pipe(
    switchMap((originalItem: TOriginal) =>
      combineLatest([of(originalItem).pipe(take(1)), combineWithObservable$])
    )
  );

@Injectable()
export class StudyTableEffects {
  studyPageData$: Observable<Action> = createEffect(() => {
    return this._actions$.pipe(
      ofType(studiesActions.fetchStudyList),
      combineWithNext(
        this._store
          .select(selectLoadedProjectId)
          .pipe(filter((pId): pId is string => !!pId))
      ),
      concatLatestFrom((x) =>
        this._store.select(getSendableStudyTableParamsDto)
      ),
      switchMap(
        ([[, projectId], sendableParams]: [
          [any, string],
          IncomingTableParamsDtoOfStudyFilters
        ]) => {
          return this._studyService
            .getTableData(projectId, sendableParams)
            .pipe(
              warnOnErrorWithRetryOption$(
                this._snackBar,
                'An error occurred while fetching studies.'
              )
            );
        }
      ),
      map((pagedStudyDetails) =>
        studiesActions.studyListUpdated(pagedStudyDetails)
      )
    );
  });

  constructor(
    private _store: Store,
    private _actions$: Actions,
    private _snackBar: MatSnackBar,
    private _studiesTableService: StudiesTableHttpService,
    private _studyService: StudyHttpService
  ) {}
}
